/**
 * Visual pattern editor: a piano-roll view of the exercise DSL. The DSL is a
 * monophonic *sequence* (steps with semitone offsets + beat lengths), so the
 * editor lays steps out left-to-right; drag a note vertically to change its
 * offset, use the per-step controls to tweak length, glide, syllable, or turn
 * it into a breathing rest. Preview plays the pattern through the same synth
 * the student player uses.
 */

import { useMemo, useRef, useState } from 'react';
import { midiToName, renderPattern, type ExercisePattern, type PatternStep } from '@solfo/shared';
import { audioContext, playExercise, type PlaybackHandle } from '../audio/synth.ts';
import { Button, Select, cls } from '../lib/ui.tsx';

const PREVIEW_ROOT = 60; // C4
const OFFSET_MIN = -12;
const OFFSET_MAX = 24;
const ROW_H = 7;
const BEAT_W = 42;
const LABEL_W = 34;
const BEAT_CHOICES = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4, 6, 8, 12];

interface Props {
  value: ExercisePattern;
  onChange: (p: ExercisePattern) => void;
}

export default function PatternEditor({ value, onChange }: Props) {
  const [selected, setSelected] = useState(0);
  const [playing, setPlaying] = useState(false);
  const dragRef = useRef<{ index: number; startY: number; startOffset: number } | null>(null);
  const handleRef = useRef<PlaybackHandle | null>(null);
  const stopTimerRef = useRef(0);

  const steps = value.steps;
  const sel: PatternStep | undefined = steps[selected];
  const totalBeats = steps.reduce((s, st) => s + st.beats, 0);
  const gridH = (OFFSET_MAX - OFFSET_MIN + 1) * ROW_H;

  function update(patch: Partial<ExercisePattern>) {
    onChange({ ...value, ...patch });
  }
  function updateStep(i: number, patch: Partial<PatternStep>) {
    update({ steps: steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  }

  function addStep(offset: number | null) {
    const last = steps[steps.length - 1];
    const step: PatternStep =
      offset === null
        ? { offset: null, beats: 2, syllable: 'breathe' }
        : { offset, beats: last?.beats ?? 1, syllable: last?.syllable };
    update({ steps: [...steps, step] });
    setSelected(steps.length);
  }

  function removeStep(i: number) {
    if (steps.length <= 1) return;
    update({ steps: steps.filter((_, j) => j !== i) });
    setSelected(Math.max(0, i - 1));
  }

  const y = (offset: number) => (OFFSET_MAX - offset) * ROW_H;

  const xs = useMemo(() => {
    const acc: { x: number; w: number }[] = [];
    let x = 0;
    for (const s of steps) {
      acc.push({ x, w: s.beats * BEAT_W });
      x += s.beats * BEAT_W;
    }
    return acc;
  }, [steps]);

  function onPointerDown(e: React.PointerEvent, i: number) {
    setSelected(i);
    const step = steps[i]!;
    if (step.offset === null) return;
    dragRef.current = { index: i, startY: e.clientY, startOffset: step.offset };
    (e.target as Element).setPointerCapture(e.pointerId);
  }
  function onPointerMove(e: React.PointerEvent) {
    const drag = dragRef.current;
    if (!drag) return;
    const delta = Math.round((drag.startY - e.clientY) / ROW_H);
    const next = Math.max(OFFSET_MIN, Math.min(OFFSET_MAX, drag.startOffset + delta));
    if (steps[drag.index]!.offset !== next) updateStep(drag.index, { offset: next });
  }
  function onPointerUp() {
    dragRef.current = null;
  }

  function preview() {
    if (playing) {
      handleRef.current?.stop();
      clearTimeout(stopTimerRef.current);
      setPlaying(false);
      return;
    }
    const rendered = renderPattern(value, PREVIEW_ROOT, value.defaultTempoBpm);
    const handle = playExercise(rendered, { tempoBpm: value.defaultTempoBpm, countInBeats: 1 });
    handleRef.current = handle;
    setPlaying(true);
    const ac = audioContext();
    const ms = (handle.startTime - ac.currentTime + rendered.totalSec + 0.3) * 1000;
    stopTimerRef.current = window.setTimeout(() => setPlaying(false), ms);
  }

  return (
    <div className="space-y-3">
      {/* piano roll */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <svg
          width={LABEL_W + totalBeats * BEAT_W + 8}
          height={gridH + 8}
          className="block touch-none select-none"
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          {/* row guides: root + octaves highlighted, in-key rows subtle */}
          {Array.from({ length: OFFSET_MAX - OFFSET_MIN + 1 }, (_, r) => {
            const offset = OFFSET_MAX - r;
            const emphasized = offset % 12 === 0;
            return (
              <g key={offset}>
                <line
                  x1={LABEL_W}
                  x2={LABEL_W + totalBeats * BEAT_W}
                  y1={r * ROW_H + 4}
                  y2={r * ROW_H + 4}
                  stroke={emphasized ? '#e2d8ce' : '#f5f1ed'}
                  strokeWidth={emphasized ? 1.5 : 1}
                />
                {emphasized && (
                  <text x={2} y={r * ROW_H + 7} fontSize={8} fill="#a89e94">
                    {midiToName(PREVIEW_ROOT + offset)}
                  </text>
                )}
              </g>
            );
          })}
          {/* beat grid */}
          {Array.from({ length: Math.ceil(totalBeats) + 1 }, (_, b) => (
            <line
              key={b}
              x1={LABEL_W + b * BEAT_W}
              x2={LABEL_W + b * BEAT_W}
              y1={4}
              y2={gridH + 4}
              stroke="#f3efeb"
            />
          ))}
          {/* steps */}
          {steps.map((s, i) => {
            const { x, w } = xs[i]!;
            const isSel = i === selected;
            if (s.offset === null) {
              return (
                <g key={i} onPointerDown={(e) => onPointerDown(e, i)} className="cursor-pointer">
                  <rect
                    x={LABEL_W + x + 1}
                    y={4}
                    width={w - 2}
                    height={gridH}
                    fill={isSel ? '#fdf1ec' : '#faf7f4'}
                    stroke={isSel ? '#d85a30' : '#e7e0d9'}
                    strokeDasharray="3 3"
                    rx={4}
                  />
                  <text
                    x={LABEL_W + x + w / 2}
                    y={gridH / 2 + 7}
                    fontSize={9}
                    fill="#a89e94"
                    textAnchor="middle"
                    fontStyle="italic"
                  >
                    {s.syllable ?? 'rest'}
                  </text>
                </g>
              );
            }
            const nextOffset = s.glide ? steps[i + 1]?.offset : null;
            return (
              <g key={i} onPointerDown={(e) => onPointerDown(e, i)} className="cursor-ns-resize">
                {/* hit area */}
                <rect x={LABEL_W + x} y={4} width={w} height={gridH} fill="transparent" />
                {typeof nextOffset === 'number' ? (
                  <line
                    x1={LABEL_W + x + 3}
                    y1={y(s.offset) + 4}
                    x2={LABEL_W + x + w - 3}
                    y2={y(nextOffset) + 4}
                    stroke="#d85a30"
                    strokeWidth={6}
                    strokeLinecap="round"
                    opacity={isSel ? 1 : 0.75}
                  />
                ) : (
                  <line
                    x1={LABEL_W + x + 3}
                    y1={y(s.offset) + 4}
                    x2={LABEL_W + x + w - 3}
                    y2={y(s.offset) + 4}
                    stroke="#d85a30"
                    strokeWidth={6}
                    strokeLinecap="round"
                    opacity={isSel ? 1 : 0.75}
                  />
                )}
                {isSel && (
                  <rect
                    x={LABEL_W + x + 0.5}
                    y={4.5}
                    width={w - 1}
                    height={gridH - 1}
                    fill="none"
                    stroke="#d85a30"
                    strokeWidth={1}
                    rx={4}
                    opacity={0.35}
                  />
                )}
                {s.syllable && w > 24 && (
                  <text
                    x={LABEL_W + x + w / 2}
                    y={Math.min(gridH - 2, y(s.offset) + 18)}
                    fontSize={9}
                    fill="#7c736a"
                    textAnchor="middle"
                  >
                    {s.syllable}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      <p className="text-xs text-slate-500">
        Tap a step to select it, drag up/down to change its pitch. Offsets are semitones from the
        movable root (labels show the C4 preview).
      </p>

      {/* selected step controls */}
      {sel && (
        <div className="flex flex-wrap items-end gap-2 rounded-xl bg-slate-50 p-2">
          <span className="text-xs font-semibold text-slate-500">Step {selected + 1}</span>
          <label className="text-xs text-slate-600">
            Length
            <Select
              className="mt-0.5 w-20 px-2 py-1 text-xs"
              value={String(sel.beats)}
              onChange={(e) => updateStep(selected, { beats: Number(e.target.value) })}
            >
              {[...new Set([...BEAT_CHOICES, sel.beats])]
                .sort((a, b) => a - b)
                .map((b) => (
                  <option key={b} value={b}>
                    {b} beat{b === 1 ? '' : 's'}
                  </option>
                ))}
            </Select>
          </label>
          <label className="text-xs text-slate-600">
            Syllable
            <input
              className="mt-0.5 block w-20 rounded-lg border border-slate-200 px-2 py-1 text-xs"
              value={sel.syllable ?? ''}
              maxLength={20}
              onChange={(e) => updateStep(selected, { syllable: e.target.value || undefined })}
            />
          </label>
          {sel.offset !== null && (
            <label className="flex items-center gap-1 pb-1 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={!!sel.glide}
                onChange={(e) => updateStep(selected, { glide: e.target.checked })}
                className="accent-brand-600"
              />
              glide → next
            </label>
          )}
          <label className="flex items-center gap-1 pb-1 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={sel.offset === null}
              onChange={(e) =>
                updateStep(
                  selected,
                  e.target.checked
                    ? { offset: null, glide: undefined, syllable: sel.syllable ?? 'breathe' }
                    : { offset: 0 },
                )
              }
              className="accent-brand-600"
            />
            breath / rest
          </label>
          <Button
            variant="ghost"
            className="px-2 py-1 text-xs text-red-600"
            onClick={() => removeStep(selected)}
            disabled={steps.length <= 1}
          >
            Remove
          </Button>
        </div>
      )}

      {/* add + ladder + preview */}
      <div className="flex flex-wrap items-end gap-2">
        <Button variant="secondary" className="px-2.5 py-1 text-xs" onClick={() => addStep(sel?.offset ?? 0)}>
          + Note
        </Button>
        <Button variant="secondary" className="px-2.5 py-1 text-xs" onClick={() => addStep(null)}>
          + Breath
        </Button>
        <label className="text-xs text-slate-600">
          Tempo
          <input
            type="number"
            min={30}
            max={240}
            className="mt-0.5 block w-16 rounded-lg border border-slate-200 px-2 py-1 text-xs"
            value={value.defaultTempoBpm}
            onChange={(e) =>
              update({ defaultTempoBpm: Math.max(30, Math.min(240, Number(e.target.value) || 100)) })
            }
          />
        </label>
        <label className="text-xs text-slate-600">
          Ladder repeats
          <input
            type="number"
            min={1}
            max={24}
            className="mt-0.5 block w-16 rounded-lg border border-slate-200 px-2 py-1 text-xs"
            value={value.ladderRepeats}
            onChange={(e) =>
              update({ ladderRepeats: Math.max(1, Math.min(24, Number(e.target.value) || 1)) })
            }
          />
        </label>
        <label className="text-xs text-slate-600">
          Ladder step
          <input
            type="number"
            min={-12}
            max={12}
            className="mt-0.5 block w-16 rounded-lg border border-slate-200 px-2 py-1 text-xs"
            value={value.ladderStep}
            onChange={(e) =>
              update({ ladderStep: Math.max(-12, Math.min(12, Number(e.target.value) || 0)) })
            }
          />
        </label>
        <Button
          variant="secondary"
          className={cls('px-2.5 py-1 text-xs', playing && 'border-brand-400 text-brand-700')}
          onClick={preview}
        >
          {playing ? '■ Stop' : '▶ Preview (from C4)'}
        </Button>
      </div>
    </div>
  );
}
